'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../../utils';
import { useDialogOverlay } from '../../hooks/dialog-stack';

type DialogScope = 'app-content' | 'viewport';

// Wrapper around Dialog.Root that fixes Radix UI pointer-events cleanup bug
const Dialog = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>
>(({ open, onOpenChange, ...props }, ref) => {
  const prevOpenRef = React.useRef(open);

  React.useEffect(() => {
    // When dialog closes (open changes from true to false)
    if (prevOpenRef.current === true && open === false) {
      // Multiple cleanup attempts to handle race conditions with nested Radix components (Select, etc)
      const cleanupPointerEvents = () => {
        document.body.style.pointerEvents = '';
      };

      // Immediate cleanup
      cleanupPointerEvents();

      // Additional cleanups at different intervals to catch race conditions
      const timer1 = setTimeout(cleanupPointerEvents, 50);
      const timer2 = setTimeout(cleanupPointerEvents, 150);
      const timer3 = setTimeout(cleanupPointerEvents, 300);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
    prevOpenRef.current = open;
  }, [open]);

  return <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props} />;
})

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

/**
 * THE dialog size scale (Max, 2026-08-20: "the same consistent-sized pop-up
 * modal for editing anything... a nice big sized component instead of that
 * right slide-out thing") — every editing/creating surface in the product
 * picks ONE of these four, never a one-off `max-w-*` at the call site:
 *
 *   sm — confirms (`max-w-md`, 28rem)
 *   md — standard forms, THE DEFAULT (`max-w-lg`, 32rem) — this is exactly
 *        the max-width every `DialogContent` rendered before `size` existed,
 *        so omitting the prop is zero behavior change for every caller.
 *   lg — wide forms (`max-w-2xl`, 42rem)
 *   xl — full entity surfaces (`max-w-5xl`, 64rem) — a bounded `h-[85vh]`
 *        flex column with overflow contained INSIDE the dialog, so the page
 *        behind it never scrolls. The two former right slide-overs
 *        (`CompanyDrawer`/`PersonDrawer`) render at this size.
 *
 * Each tier keeps the pre-existing responsive shrink (`min(Nrem, 100vw -
 * inset - 2rem)`) so a dialog never overflows a narrow viewport, scoped by
 * `scope` exactly like the un-sized max-width was. `className` still
 * composes/overrides last via `cn` (tailwind-merge), so a caller's own
 * `max-w-*` continues to win.
 */
type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const DIALOG_SIZE_MAX_WIDTH: Record<DialogSize, { appContent: string; viewport: string }> = {
  sm: {
    appContent: 'max-w-[min(28rem,100vw_-_var(--app-content-inset,48px)_-_2rem)]',
    viewport: 'max-w-[min(28rem,100vw_-_2rem)]',
  },
  md: {
    appContent: 'max-w-[min(32rem,100vw_-_var(--app-content-inset,48px)_-_2rem)]',
    viewport: 'max-w-[min(32rem,100vw_-_2rem)]',
  },
  lg: {
    appContent: 'max-w-[min(42rem,100vw_-_var(--app-content-inset,48px)_-_2rem)]',
    viewport: 'max-w-[min(42rem,100vw_-_2rem)]',
  },
  xl: {
    appContent: 'max-w-[min(64rem,100vw_-_var(--app-content-inset,48px)_-_2rem)]',
    viewport: 'max-w-[min(64rem,100vw_-_2rem)]',
  },
};

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & { scope?: DialogScope }
>(({ className, scope = 'app-content', style, ...props }, ref) => {
  const showOverlay = useDialogOverlay();

  // Console dialogs cover only the interactive app region. Public surfaces do
  // not have that shell, so they can opt into a true viewport modal instead of
  // inheriting the console's global sidebar and top-nav CSS variables.
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed right-0 bottom-0 z-modal data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        // Only show dark background for first dialog
        showOverlay && 'bg-brand-navy/55',
        className
      )}
      style={{
        ...(scope === 'viewport'
          ? { inset: 0 }
          : {
              top: 'var(--app-topnav-h, 48px)',
              left: 'var(--app-content-inset, 48px)',
            }),
        ...style,
      }}
      {...props}
    />
  );
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean;
    scope?: DialogScope;
    /** THE size scale — see the comment above `DialogSize`. Defaults to
     *  `'md'`, which reproduces the pre-`size` max-width exactly. */
    size?: DialogSize;
  }
>(({ className, children, hideClose, scope = 'app-content', size = 'md', style, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay scope={scope} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-modal grid w-full translate-x-[-50%] translate-y-[-50%] gap-3 border bg-background p-6 shadow-lg outline-none duration-200 focus-visible:outline-none focus-visible:ring-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        // Keep long content from widening the implicit grid column past the dialog.
        'grid-cols-1 overflow-y-auto',
        scope === 'viewport'
          ? DIALOG_SIZE_MAX_WIDTH[size].viewport
          : DIALOG_SIZE_MAX_WIDTH[size].appContent,
        // xl is a full entity surface: bounded height, flex column, the
        // reader scrolls INSIDE the dialog and the page behind it never
        // scrolls. Smaller tiers grow naturally up to maxHeight, then scroll
        // as a whole so long forms and their actions stay reachable.
        size === 'xl' && 'flex h-[85vh] flex-col overflow-hidden',
        className
      )}
      style={{
        ...(scope === 'viewport'
          ? {
              top: '50%',
              left: '50%',
              maxHeight: 'calc(100vh - 2rem)',
            }
          : {
              // Center within the post-shell content region: viewport center
              // shifted right by half the sidebar and down by half the top nav.
              top: 'calc(50% + var(--app-topnav-h, 48px) / 2)',
              left: 'calc(50% + var(--app-content-inset, 48px) / 2)',
              maxHeight: 'calc(100vh - var(--app-topnav-h, 48px) - 2rem)',
            }),
        ...style,
      }}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className="focus-ring absolute right-1.5 top-1.5 rounded-sm p-2.5 opacity-70 transition-all hover:opacity-100 hover:bg-surface-hover hover:text-ink-supporting disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-ink-supporting">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left pb-4',
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-2xl font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-ink-supporting', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
